import { forwardRef, PropsWithChildren, ReactNode, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { XOR } from 'utils';

type PopoverProps = XOR<{ content: ReactNode }, PropsWithChildren> & {
  trigger: any;
  triggerClassNames: string;
  ['data-cy']?: string;
};
interface PopoverRef {
  hide: () => void;
}

export const Popover = forwardRef<PopoverRef, PopoverProps>(
  ({ triggerClassNames, trigger, content, children, ...props }, forwardedRef) => {
    const [visible, setVisible] = useState(false);
    const popoverRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);

    const toggleVisibility = () => {
      setVisible(visible => !visible);
    };

    useImperativeHandle(forwardedRef, () => ({ hide: () => setVisible(false) }), [setVisible]);

    useEffect(() => {
      const handleClickOutside = (event: any) => {
        if (!!popoverRef.current && !!triggerRef.current) {
          const popoverRect = popoverRef.current.getBoundingClientRect();
          const triggerRect = triggerRef.current.getBoundingClientRect();
          const clickX = event.clientX;
          const clickY = event.clientY;
          const isInPopoverRect =
            popoverRect.top <= clickY &&
            popoverRect.bottom >= clickY &&
            popoverRect.left <= clickX &&
            popoverRect.right >= clickX;
          const isInTriggerRect =
            triggerRect.top <= clickY &&
            triggerRect.bottom >= clickY &&
            triggerRect.left <= clickX &&
            triggerRect.right >= clickX;
          if (!isInPopoverRect && !isInTriggerRect) {
            setVisible(false);
          }
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    return (
      <div className="popover-container d-flex" data-cy={props['data-cy']}>
        <button ref={triggerRef} onClick={toggleVisibility} className={triggerClassNames} data-cy="popover-trigger">
          {trigger}
        </button>
        {visible && (
          <div id="popover-content" ref={popoverRef} className="popover-content">
            {content ?? children}
          </div>
        )}
      </div>
    );
  }
);
